import { useFormContext, useFormState, useWatch } from "react-hook-form";

import { AGENT_SUBJECTS, goalTypeSubject } from "@/shared/domain/agents";
import { useOrgEventModel } from "@/shared/domain/orgs/hooks";
import { X } from "@/shared/foundation/icons";
import {
  Banner,
  Card,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";

import {
  eventsGoalRefusal,
  type AgentFormValues,
  type GoalFormValues,
  type GoalKind,
} from "../agentFormSchema";

import { CreateEventsGoalForm } from "./CreateEventsGoalForm";
import { GOAL_KIND_COPY, GOAL_KINDS, offeredGoalKinds } from "./goalKinds";
import styles from "./goals.module.css";
import { ImplicitBinding } from "./ImplicitBinding";
import { SummaryGoalForm } from "./SummaryGoalForm";
import { TriageGoalForm } from "./TriageGoalForm";

export interface GoalCardProps {
  index: number;
  onRemove: () => void;
  /** Replaces this goal with a fresh one of `kind`, keeping its position. */
  onChangeKind: (kind: GoalKind) => void;
}

/**
 * One goal: the kind it is, and the fields that kind needs.
 *
 * The section owns which goals exist; the card owns the fields of its own.
 */
export function GoalCard({ index, onRemove, onChangeKind }: GoalCardProps) {
  const { control, getFieldState } = useFormContext<AgentFormValues>();
  const goal = useWatch<AgentFormValues>({
    control,
    name: `goals.${index}`,
  }) as GoalFormValues;
  const kind = goal.goal_type;
  const subject = goalTypeSubject(goal.goal_type);
  const agentSubject = useWatch({ control, name: "subject" });
  const formState = useFormState<AgentFormValues>({ control });
  const kindError = getFieldState(`goals.${index}.goal_type`, formState).error;
  const eventModel = useOrgEventModel();
  const offered = offeredGoalKinds(agentSubject, eventModel);
  // A saved events goal that the org's event model does not allow stays as
  // it was saved, with a banner saying why it cannot run.
  const refusal = eventsGoalRefusal(kind, eventModel);
  // The goal's own kind keeps its radio even when it is not on offer (a
  // refused events goal, or a goal on the other kind of record than the agent
  // runs on), so the picker still shows what the goal is.
  const shownKinds = GOAL_KINDS.filter(
    (candidate) => offered.includes(candidate) || candidate === kind,
  );

  return (
    <Card.Root
      size="compact"
      data-testid={`agent-goal-${index}`}
      data-kind={kind}
    >
      <Card.Header>
        {/* The card's title in the page outline; the blocks under it are h4s. */}
        <h3 className={styles.goalHeading}>
          <Card.Title>Goal</Card.Title>{" "}
          <Card.Summary>{GOAL_KIND_COPY[kind].label}</Card.Summary>
        </h3>
        <Card.Actions>
          <RobotoButton
            variant="ghost"
            icon={<X aria-hidden />}
            aria-label="Remove this goal"
            onClick={onRemove}
            data-testid={`agent-goal-${index}-remove`}
          />
        </Card.Actions>
      </Card.Header>
      <Card.Body className={styles.goalBody}>
        <RadioGroup<GoalKind>
          appearance="card"
          size="md"
          aria-label="What this goal is"
          value={kind}
          onValueChange={onChangeKind}
          // The banner below already says why a refused kind cannot run.
          error={refusal === undefined ? kindError?.message : undefined}
        >
          {shownKinds.map((shown) => {
            const { label, description, icon: Icon } = GOAL_KIND_COPY[shown];
            return (
              <Radio
                key={shown}
                value={shown}
                label={label}
                description={description}
                icon={<Icon aria-hidden size={16} />}
              />
            );
          })}
        </RadioGroup>

        {refusal !== undefined && (
          <Banner
            variant="warning"
            title="The organization's event model does not allow this goal"
            description={refusal}
            data-testid={`agent-goal-${index}-refused`}
          />
        )}

        {/* Every kind of goal works on one dataset or one session, always the
            one chosen at launch, so the binding is stated once, above the
            kind's own fields, rather than inside each of them. */}
        <ImplicitBinding
          label={AGENT_SUBJECTS[subject].label}
          placeholder={AGENT_SUBJECTS[subject].placeholder}
          hint={`The ${subject} the agent is launched on.`}
        />

        {(kind === "dataset_summary" || kind === "session_summary") && (
          <SummaryGoalForm index={index} />
        )}
        {(kind === "dataset_triage" || kind === "session_triage") && (
          <TriageGoalForm index={index} subject={subject} />
        )}
        {(kind === "create_events" || kind === "session_create_events") && (
          <CreateEventsGoalForm index={index} kind={kind} />
        )}
      </Card.Body>
    </Card.Root>
  );
}
